import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactForm = ({ 
  customer,
  reviewData, 
  onSubmit, 
  onBack, 
  submitting, 
  error 
}) => {
  const [contactData, setContactData] = useState({
    message: '',
    preferredContact: 'email',
    requestCallback: false,
    urgency: 'normal'
  });

  const handleInputChange = (field, value) => {
    setContactData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!contactData.message.trim()) {
      return;
    }

    onSubmit(contactData);
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starNumber = index + 1;
      const isFilled = starNumber <= reviewData.rating;
      
      return (
        <span
          key={starNumber}
          className={`text-lg ${isFilled ? 'text-yellow-400' : 'text-gray-300'}`}
        >
          ★
        </span>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <Helmet>
        <title>Help Us Improve - ServiceFlow Pro</title>
      </Helmet>
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="MessageCircle" size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            We want to make this right
          </h1>
          <p className="text-gray-600">
            Your feedback is important to us. Let us know how we can improve and make this right.
          </p>
        </div>

        {/* Review Summary */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="text-center mb-4">
            <div className="flex justify-center mb-2">
              {renderStars()}
            </div>
            <h3 className="font-semibold text-gray-900">Your Experience</h3>
          </div>
          
          {reviewData.comment && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-gray-700 italic">"{reviewData.comment}"</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <strong>Service:</strong> {customer?.service_name || 'Service'}
            </div>
            <div>
              <strong>Location:</strong> {customer?.address || 'Service Location'}
            </div>
            <div>
              <strong>Date:</strong> {customer?.service_date ? new Date(customer.service_date).toLocaleDateString() : new Date().toLocaleDateString()}
            </div>
            <div>
              <strong>Customer:</strong> {reviewData.reviewer_name}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Tell us what went wrong
          </h2>
          
          <form onSubmit={handleSubmit}>
            {/* Message */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Please describe the issue and how we can improve *
              </label>
              <textarea
                value={contactData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder="Help us understand what went wrong and how we can make it better..."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Your feedback will be kept private and used to improve our services.
              </p>
            </div>

            {/* Urgency Level */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How urgent is this issue?
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { value: 'normal', label: 'Normal', desc: 'General feedback' },
                  { value: 'urgent', label: 'Urgent', desc: 'Needs quick attention' },
                  { value: 'critical', label: 'Critical', desc: 'Immediate response needed' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                      contactData.urgency === option.value 
                        ? 'border-primary bg-primary-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="urgency"
                      value={option.value}
                      checked={contactData.urgency === option.value}
                      onChange={(e) => handleInputChange('urgency', e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-500">{option.desc}</div>
                    </div>
                    {contactData.urgency === option.value && (
                      <Icon name="CheckCircle" size={20} className="text-primary" />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Preferred Contact Method */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How would you prefer we contact you?
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'email', label: 'Email', icon: 'Mail' },
                  { value: 'phone', label: 'Phone Call', icon: 'Phone' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                      contactData.preferredContact === option.value 
                        ? 'border-primary bg-primary-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="preferredContact"
                      value={option.value}
                      checked={contactData.preferredContact === option.value}
                      onChange={(e) => handleInputChange('preferredContact', e.target.value)}
                      className="sr-only"
                    />
                    <Icon name={option.icon} size={20} className="text-gray-400 mr-3" />
                    <span className="font-medium text-gray-900">{option.label}</span>
                    {contactData.preferredContact === option.value && (
                      <Icon name="CheckCircle" size={20} className="text-primary ml-auto" />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Request Callback */}
            <div className="mb-6">
              <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300">
                <input
                  type="checkbox"
                  checked={contactData.requestCallback}
                  onChange={(e) => handleInputChange('requestCallback', e.target.checked)}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Request immediate callback</div>
                  <div className="text-sm text-gray-500">A manager will call you within 24 hours</div>
                </div>
              </label>
            </div>

            {/* Contact Information Display */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Your Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Email:</span> {reviewData.reviewer_email || 'Not provided'}
                </div>
                <div>
                  <span className="text-gray-500">Phone:</span> {reviewData.reviewer_phone || 'Not provided'}
                </div>
              </div>
              {(!reviewData.reviewer_email || !reviewData.reviewer_phone) && (
                <p className="text-sm text-amber-600 mt-2">
                  <Icon name="AlertTriangle" size={16} className="inline mr-1" />
                  Missing contact info may delay our response.
                </p>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={16} className="text-red-600 mr-2" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                variant="primary"
                disabled={submitting || !contactData.message.trim()}
                className="flex-1"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending feedback...
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={16} className="mr-2" />
                    Send Feedback
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                onClick={onBack}
                variant="ghost"
                disabled={submitting}
                className="sm:w-auto"
              >
                ← Back to rating
              </Button>
            </div>
          </form>
        </div>

        {/* Assurance Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" size={24} className="text-blue-600 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">We're committed to making this right</h3>
              <p className="text-blue-800 text-sm">
                Your feedback helps us improve. A member of our team will review your concerns and contact you 
                {contactData.urgency === 'critical' ? ' within 4 hours' : 
                  contactData.urgency === 'urgent' ? ' within 24 hours' : 
                  ' within 2 business days'}.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Thank you for giving us the opportunity to improve</p>
        </div>
      </div>
    </div>
  );
};

export default ContactForm; 